import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { cssMap } from '@atlaskit/css';
import DatePicker from '@atlaskit/datetime-picker/date-picker';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';

import { asDateValue } from '../../../../../../../../common/types/form/form-fields/field-values/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import { startOfWeekDay } from '../../../../../../../../common/utils';
import {
	useCurrentValue,
	useFieldInvalid,
	useFieldRequired,
	useFormField,
	useFormRendererActions,
} from '../../../../../../../../controllers/form-renderer-store';
import { messages } from './messages';

const styles = cssMap({
	container: {
		maxWidth: '160px',
	},
});

export const EditDateInput = ({
	fieldId,
	fieldInputId,
	fieldErrorId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useCurrentValue);
	const isInvalid = useFieldInvalid({ fieldId });
	const currentValue = useCurrentValue({ fieldId });
	const formField = useFormField({ fieldId });
	const { updateModifiedValue } = useFormRendererActions();
	const intl = useIntl();
	const isRequired = useFieldRequired({ fieldId });

	const onChange = useCallback(
		(value: string) => {
			if (value.length === 0) {
				updateModifiedValue(fieldId, null);
			} else {
				updateModifiedValue(fieldId, { date: value });
			}
		},
		[fieldId, updateModifiedValue],
	);

	return (
		<Box xcss={styles.container}>
			<DatePicker
				id={fieldInputId}
				value={asDateValue(currentValue)?.date ?? ''}
				isInvalid={isInvalid}
				onChange={onChange}
				locale={intl.locale}
				placeholder={intl.formatMessage(messages.datePlaceholder, {
					date: intl.formatDate(new Date(), {}),
				})}
				weekStartDay={startOfWeekDay(intl.locale)}
				testId={`field-input-date-edit-${fieldId}`}
				shouldShowCalendarButton={!currentValue}
				isRequired={isRequired}
				aria-label={
					formField?.label ||
					intl.formatMessage(messages.datePlaceholder, {
						date: intl.formatDate(new Date(), {}),
					})
				}
				aria-describedby={
					isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
				}
			/>
		</Box>
	);
};
